import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, MapPin, Phone, Search, Loader2, Calendar, Clock } from "lucide-react";

const STAGE_COLORS = {
  prospect_identified: "bg-slate-100 text-slate-700", initial_contact: "bg-blue-100 text-blue-700",
  qualified: "bg-indigo-100 text-indigo-700", meeting_scheduled: "bg-violet-100 text-violet-700",
  presented_demo: "bg-purple-100 text-purple-700", verbal_commitment: "bg-amber-100 text-amber-700",
  onboarding_in_progress: "bg-emerald-100 text-emerald-700", live_published: "bg-green-100 text-green-700",
  stalled_lost: "bg-red-100 text-red-700",
};
const TIER_COLORS = { A: "bg-green-500", B: "bg-blue-500", C: "bg-amber-500" };

export default function CoordinatorProspects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");

  useEffect(() => {
    api.get("/recruitment/prospects").then(r => setProspects(r.data.prospects || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = prospects.filter(p => {
    if (search && !p.venue_name.toLowerCase().includes(search.toLowerCase()) && !p.municipality?.toLowerCase().includes(search.toLowerCase())) return false;
    if (stageFilter && p.current_stage !== stageFilter) return false;
    if (tierFilter && p.tier !== tierFilter) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5" data-testid="coordinator-prospects">
      <h1 className="font-heading text-2xl sm:text-3xl flex items-center gap-2"><Building2 className="w-6 h-6 text-primary" />My Prospects</h1>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search venue or city..." className="pl-9" />
        </div>
        <Select value={stageFilter} onValueChange={v => setStageFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All Stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {["prospect_identified","initial_contact","qualified","meeting_scheduled","presented_demo","verbal_commitment","onboarding_in_progress","live_published","stalled_lost"].map(s => (
              <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tierFilter} onValueChange={v => setTierFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-28"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {["A","B","C"].map(t => <SelectItem key={t} value={t}>Tier {t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground">{filtered.length} prospect{filtered.length !== 1 ? "s" : ""}</p>

      {/* List */}
      <div className="space-y-2">
        {filtered.map(p => (
          <Card key={p.prospect_id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate(`/recruitment?detail=${p.prospect_id}`)}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-sm font-medium truncate">{p.venue_name}</h3>
                  <Badge className={STAGE_COLORS[p.current_stage] || "bg-muted"} variant="secondary">{p.current_stage?.replace(/_/g," ")}</Badge>
                  <span className={`w-5 h-5 rounded-full text-white text-[9px] flex items-center justify-center font-bold ${TIER_COLORS[p.tier]}`}>{p.tier}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.municipality || "—"}</span>
                  {p.contact_person_name && <span>{p.contact_person_name}</span>}
                  {p.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{p.phone}</span>}
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                {p.next_follow_up_date && <p className="text-[10px] text-blue-600 flex items-center gap-0.5 justify-end"><Calendar className="w-3 h-3" />{p.next_follow_up_date?.slice(0,10)}</p>}
                {p.onboarding_progress > 0 && <p className="text-[10px] text-emerald-600">{p.onboarding_progress}% onboarded</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
